import { useCallback, useEffect, useState } from "react";
import { useWatchlists, Watchlist } from "./useWatchlists";

const DEFAULT_STORAGE_KEY = "insight:selected-watchlist-id";

// Which watchlist the user is looking at is a per-device view preference, not
// account state — so it lives in localStorage rather than a DB column. Storage
// can throw in private mode and in the Capacitor WebView, so both accessors
// degrade to "no preference" rather than breaking the dashboard.
const readStoredId = (storageKey: string): string | null => {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

const writeStoredId = (storageKey: string, watchlistId: string): void => {
  try {
    localStorage.setItem(storageKey, watchlistId);
  } catch {
    // Preference just won't persist to the next launch.
  }
};

interface SelectedWatchlistOptions {
  /**
   * Where this surface remembers its choice. Surfaces that pass their own key
   * get an independent selection — switching lists on Earnings must not move
   * the dashboard out from under the user.
   */
  storageKey?: string;
  /**
   * First-run preference: pick the list with this name (case-insensitive) when
   * nothing has been stored yet. Only ever applies before the user has chosen —
   * once they have, the stored id always wins, so renaming that list later
   * changes nothing.
   */
  preferName?: string;
}

interface SelectedWatchlist {
  watchlists: Watchlist[];
  selectedWatchlist: Watchlist | null;
  selectedWatchlistId: string | null;
  selectWatchlist: (watchlistId: string | null) => void;
  isLoading: boolean;
}

/**
 * Resolves which watchlist a surface is showing.
 *
 * The selection is *derived*, never trusted from storage: a stored id that was
 * deleted in another tab, deleted on another device, or belongs to a previous
 * account simply fails the lookup and falls back to the first watchlist. Since
 * the API returns them oldest-first, that fallback is always the signup default
 * and never moves when a new list is created.
 */
export function useSelectedWatchlist(options: SelectedWatchlistOptions = {}): SelectedWatchlist {
  const { storageKey = DEFAULT_STORAGE_KEY, preferName } = options;
  const { data, isLoading } = useWatchlists();
  const [storedId, setStoredId] = useState<string | null>(() => readStoredId(storageKey));

  const watchlists = data ?? [];
  const preferred = preferName
    ? watchlists.find(
        (watchlist) => watchlist.name.toLowerCase() === preferName.toLowerCase(),
      )
    : undefined;
  const selectedWatchlist =
    watchlists.find((watchlist) => watchlist.id === storedId) ??
    preferred ??
    watchlists[0] ??
    null;

  // Persist whatever the derivation settled on, so a healed fallback sticks.
  // Nothing rendered depends on this having run.
  useEffect(() => {
    if (selectedWatchlist && selectedWatchlist.id !== storedId) {
      writeStoredId(storageKey, selectedWatchlist.id);
      setStoredId(selectedWatchlist.id);
    }
  }, [selectedWatchlist, storedId, storageKey]);

  const selectWatchlist = useCallback(
    (watchlistId: string | null) => {
      setStoredId(watchlistId);
      if (watchlistId) {
        writeStoredId(storageKey, watchlistId);
      }
    },
    [storageKey],
  );

  return {
    watchlists,
    selectedWatchlist,
    selectedWatchlistId: selectedWatchlist?.id ?? null,
    selectWatchlist,
    isLoading,
  };
}
